import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import SearchHeader from '../components/Search/SearchHeader';
import UniverseButton from '../components/Search/UniverseButton';
import MatcheeCard from '../components/Search/MatcheeCard';
import EmptyState from '../components/Search/EmptyState';
import PageLoader from './PageLoader';

import useUsersResource from '../resources/useUsersResource';
import type { User } from '../resources/types/usersTypes';

interface SearchPageState {
  matchees: User[];
  filteredMatchees: User[];
  searchQuery: string;
  isLoading: boolean;
  isRefreshing: boolean;
  isUniverseLoading: boolean;
  isSearching: boolean;
  error: string | null;
  errorType: 'network' | 'server' | 'unknown' | null;
  retryCount: number;
}

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { randomMatches, universeMatch, searchHandle } = useUsersResource();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to categorize errors and provide appropriate messages
  const handleError = useCallback((error: unknown): { message: string; type: 'network' | 'server' | 'unknown' } => {
    console.error('SearchPage error:', error);

    if (error instanceof AxiosError) {
      // Network/connection errors
      if (!error.response) {
        return {
          message: t('networkError', 'Network connection failed. Please check your internet connection.'),
          type: 'network'
        };
      }

      // Server errors (500+)
      if (error.response.status >= 500) {
        return {
          message: t('serverError', 'Server error occurred. Please try again later.'),
          type: 'server'
        };
      }

      // Client errors (400-499)
      if (error.response.status >= 400) {
        return {
          message: t('clientError', 'Request failed. Please try again.'),
          type: 'server'
        };
      }
    }

    // Generic error fallback
    return {
      message: t('searchError', 'Failed to load matchees'),
      type: 'unknown'
    };
  }, [t]);

  const [state, setState] = useState<SearchPageState>({
    matchees: [],
    filteredMatchees: [],
    searchQuery: '',
    isLoading: true,
    isRefreshing: false,
    isUniverseLoading: false,
    isSearching: false,
    error: null,
    errorType: null,
    retryCount: 0,
  });

  // Fetch random matchees from API with retry logic
  const fetchUsers = useCallback(async (isRetry: boolean = false) => {
    try {
      setState(prev => ({
        ...prev,
        error: null,
        errorType: null,
        retryCount: isRetry ? prev.retryCount + 1 : 0
      }));

      const matcheesData = await randomMatches();
      setState(prev => ({
        ...prev,
        matchees: matcheesData,
        filteredMatchees: matcheesData,
        isLoading: false,
        isRefreshing: false,
        error: null,
        errorType: null,
      }));
    } catch (error) {
      const { message, type } = handleError(error);

      setState(prev => ({
        ...prev,
        matchees: [],
        filteredMatchees: [],
        error: message,
        errorType: type,
        isLoading: false,
        isRefreshing: false,
      }));
    }
  }, [randomMatches, handleError]);

  // Search users by handle
  const searchUsers = useCallback(async (query: string) => {
    try {
      setState(prev => ({
        ...prev,
        isSearching: true,
        error: null,
        errorType: null
      }));

      const searchResults = await searchHandle(query);
      setState(prev => ({
        ...prev,
        filteredMatchees: searchResults,
        isSearching: false,
        error: null,
        errorType: null,
      }));
    } catch (error) {
      const { message, type } = handleError(error);

      setState(prev => ({
        ...prev,
        filteredMatchees: [],
        isSearching: false,
        error: message,
        errorType: type,
      }));
    }
  }, [searchHandle, handleError]);

  // Initial data loading
  useEffect(() => {
    fetchUsers(false);
  }, [fetchUsers]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle search with debouncing
  const handleSearch = useCallback((query: string) => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setState(prev => ({
      ...prev,
      searchQuery: query,
    }));

    const trimmedQuery = query.trim();

    // If search is empty, go back to random matches
    if (trimmedQuery === '') {
      setState(prev => ({
        ...prev,
        filteredMatchees: prev.matchees,
        error: null,
        errorType: null,
      }));
      return;
    }

    // Debounce search API calls
    searchTimeoutRef.current = setTimeout(() => {
      searchUsers(trimmedQuery);
    }, 300); // 300ms delay
  }, [searchUsers]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate('/sangati');
  }, [navigate]);

  // Handle refresh functionality
  const handleRefresh = useCallback(async () => {
    const currentSearchQuery = state.searchQuery;
    setState(prev => ({ ...prev, isRefreshing: true }));

    try {
      await fetchUsers(false); // Not a retry, just a refresh
      // Reapply search filter after refresh if there was a search query
      if (currentSearchQuery.trim() !== '') {
        handleSearch(currentSearchQuery);
      }
    } catch (error) {
      // Error is already handled in fetchUsers
      console.error('Refresh failed:', error);
    }
  }, [fetchUsers, state.searchQuery, handleSearch]);

  // Handle universe match
  const handleUniverseMatch = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isUniverseLoading: true }));
      const matchedUser = await universeMatch();
      // Navigate to Sangati page with matched user
      // Don't pass modeMatch so it uses userData flow
      navigate('/sangati', {
        state: {
          matchHandle: matchedUser.handle,
          userData: {
            light_mode_active: matchedUser.light_mode_active,
            dark_mode_active: matchedUser.dark_mode_active
          }
        }
      });
    } catch (error) {
      console.error('Universe match failed:', error);
      // Error handling is managed by the UniverseButton component
    } finally {
      setState(prev => ({ ...prev, isUniverseLoading: false }));
    }
  }, [universeMatch, navigate]);

  // Handle matchee selection
  const handleMatcheeSelect = useCallback((handle: string) => {
    // Find the user data from the existing matchees state
    const selectedUser = state.matchees.find(user => user.handle === handle);

    if (selectedUser) {
      // Navigate to Sangati page with user data from existing state
      // Don't pass modeMatch so it uses userData flow
      navigate('/sangati', {
        state: {
          matchHandle: handle,
          userData: {
            light_mode_active: selectedUser.light_mode_active,
            dark_mode_active: selectedUser.dark_mode_active
          }
        }
      });
    } else {
      // Fallback to just passing the handle if user not found in state
      navigate('/sangati', {
        state: {
          matchHandle: handle
        }
      });
    }
  }, [navigate, state.matchees]);

  // Handle retry for error states with exponential backoff
  const handleRetry = useCallback(() => {
    const { retryCount } = state;

    // Limit retry attempts to prevent infinite loops
    if (retryCount >= 3) {
      setState(prev => ({
        ...prev,
        error: t('maxRetriesReached', 'Maximum retry attempts reached. Please refresh the page.'),
        errorType: 'unknown'
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null, errorType: null }));

    // Add exponential backoff delay
    const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
    setTimeout(() => {
      fetchUsers(true);
    }, delay);
  }, [fetchUsers, state, t]);

  const {
    filteredMatchees,
    searchQuery,
    isLoading,
    isRefreshing,
    isUniverseLoading,
    isSearching,
    error,
    errorType,
    retryCount
  } = state;

  // Show loading screen during initial load
  if (isLoading) {
    return <PageLoader />;
  }

  // Determine if UI should be disabled during loading operations
  const isDisabled = isRefreshing || isUniverseLoading || isSearching;

  return (
    <div className="relative w-full max-w-[786px] mx-auto h-dvh overflow-hidden flex flex-col bg-bg-grey">
      {/* Search Header */}
      <SearchHeader
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        onBack={handleBack}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        disabled={isDisabled}
      />

      {/* Universe Button */}
      <div className="px-3 py-3 sm:px-4 sm:py-4">
        <UniverseButton
          onUniverseMatch={handleUniverseMatch}
          isLoading={isUniverseLoading}
          disabled={isDisabled}
        />
      </div>

      {/* Content Area */}
      <div className="flex-1 px-3 pb-4 overflow-y-auto sm:px-4">
        {error ? (
          <EmptyState
            type="error"
            message={error}
            onRetry={retryCount < 3 ? handleRetry : undefined}
            errorType={errorType}
            retryCount={retryCount}
          />
        ) : !filteredMatchees || filteredMatchees.length === 0 ? (
          <EmptyState
            type={searchQuery.trim() !== '' ? 'no-results' : 'no-matchees'}
            message={searchQuery.trim() !== '' ? t('noUserFound') : t('noMatcheesAvailable')}
          />
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {filteredMatchees?.map((matchee) => (
              <MatcheeCard
                key={matchee.handle}
                matchee={matchee}
                onClick={handleMatcheeSelect}
                disabled={isDisabled}
              />
            )) || []}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
